import type { ShareableFieldValue } from '@amzn/journeys-common';
import { Box, Checkbox, Column, Link, Row, Text } from '@amzn/meridian';
import { FormLabelWithOptional } from 'components/form/FormLabelWithOptional';
import { JFormInput } from 'components/form/JFormInput';
import type { ShareableFieldConfigBase } from 'features/traffic/campaigns/campaign-configs/types';
import { stylesheet } from 'typestyle';
import { checkIfMeridianSelectOptionClicked } from 'utils/check-if-meridian-select-option-clicked';
import { SharedVarWrapper } from '../../shared-variables/SharedVarWrapper';
import { GeneratedRefTagDisplay } from './GeneratedRefTagDisplay';
import { LinkTypeAndLinkTypeValueEditor } from './LinkTypeAndLinkTypeValueEditor';
import { useRefTaggedLinkEditor } from './useRefTaggedLinkEditor';

type RefTaggedLinkEditorProps<TFieldValue extends ShareableFieldValue, TFlavorContent extends Record<string, unknown>> = {
  name: string;
  className?: string;
  areaIndex?: number;
  shareableFieldConfig: ShareableFieldConfigBase<TFieldValue, TFlavorContent>;
  removeUrlRefTag?: boolean;
  refTagAutoGenSuffix?: string;
  customRefTagGenerator?: () => string;
};

const css = stylesheet({
  dealsContent: {
    paddingRight: '2.5rem',
    paddingTop: '2.1rem',
  },
});

/**
 * RefTaggedLinkEditor is a component that allows the user to edit the ref-tagged link.
 * - This is herotator specific because of the URL generation
 * - May need to make this more generic if we want to support other URL generation patterns
 */
export const RefTaggedLinkEditor = <TFieldValue extends ShareableFieldValue, TFlavorContent extends Record<string, unknown>>({
  name,
  className,
  areaIndex,
  shareableFieldConfig,
  removeUrlRefTag = false,
  refTagAutoGenSuffix,
  customRefTagGenerator,
}: RefTaggedLinkEditorProps<TFieldValue, TFlavorContent>) => {
  const {
    generatedRefTag,
    gpLink,
    validateLinkTypeAndValue,
    refTaggedLink,
    generatedDestinationUrl,
    destinationUrlId,
    destinationUrlInvalid,
    destinationUrlLink,
    autoGenerateRefTagField,
    renderDealsWarningModal,
  } = useRefTaggedLinkEditor({
    areaIndex,
    customRefTagGenerator,
    name,
    refTagAutoGenSuffix,
    removeUrlRefTag,
    shareableFieldConfig,
  });

  const handleChangeAutoGenRefTag = (newAutoGenerateRefTag: boolean) => {
    autoGenerateRefTagField.input.onChange(newAutoGenerateRefTag);
  };

  return (
    <div className={className}>
      <SharedVarWrapper
        activeSharedVarValidator={validateLinkTypeAndValue}
        checkIgnoreBlurClick={checkIfMeridianSelectOptionClicked}
        name={gpLink('linkTypeAndLinkTypeValue')}
        shareableFieldConfig={shareableFieldConfig}
      >
        <LinkTypeAndLinkTypeValueEditor name={gpLink('linkTypeAndLinkTypeValue.value')} renderDealsWarningModal={renderDealsWarningModal} />
      </SharedVarWrapper>

      <Row
        alignmentVertical="stretch"
        widths={[
          'fill',
          'fit',
        ]}
      >
        <Column>
          {refTaggedLink.autoGenerateRefTag ? (
            <GeneratedRefTagDisplay refTag={generatedRefTag} />
          ) : (
            <JFormInput label="Ref tag" name={gpLink('refTag')} />
          )}
        </Column>
        <Column className={css.dealsContent}>
          <Checkbox checked={refTaggedLink.autoGenerateRefTag} onChange={handleChangeAutoGenRefTag} size="small">
            Auto-generate
          </Checkbox>
        </Column>
      </Row>

      <Box spacingInset="400 none none">
        <FormLabelWithOptional id={destinationUrlId} label="Preview of destination URL" />

        <Text color="secondary" type="b200">
          <Link
            aria-labelledby={destinationUrlId}
            disabled={destinationUrlInvalid}
            href={destinationUrlLink}
            rel="noreferrer"
            target="_blank"
            type="secondary"
          >
            {generatedDestinationUrl}
          </Link>
          {destinationUrlInvalid && (
            <>
              &nbsp;
              <Text color="secondary" tag="span">
                (1 or more missing or invalid values)
              </Text>
            </>
          )}
        </Text>
      </Box>
    </div>
  );
};
